import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteTask, listAssignedToMe, listCreatedTasks, sendNotification } from "./AssignedTaskService";
import "./TasksPage.css";

const STATUS_COLORS = {
  PENDING: "status-pending",
  IN_PROGRESS: "status-in-progress",
  DONE: "status-done",
  CANCELLED: "status-cancelled",
};

const PRIORITY_COLORS = {
  CRITICAL: "priority-critical",
  HIGH: "priority-high",
  MEDIUM: "priority-medium",
  LOW: "priority-low",
  SOMEDAY: "priority-someday",
};

export default function TasksPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("created");
  const [createdTasks, setCreatedTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const [created, assigned] = await Promise.all([listCreatedTasks(), listAssignedToMe()]);
    setCreatedTasks(created);
    setAssignedTasks(assigned);
    setLoading(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete task "${title}"? This cannot be undone.`)) return;
    await deleteTask(id);
    setCreatedTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const handleNotify = async (id) => {
    setNotifyingId(id);
    await sendNotification(id);
    setNotifyingId(null);
  };

  const tasks = tab === "created" ? createdTasks : assignedTasks;

  return (
    <div className="tasks-page">
      <div className="tasks-page-header">
        <h2>Tasks</h2>
        <button className="tasks-page-new-btn" onClick={() => nav("new")}>
          + Assign New Task
        </button>
      </div>

      <div className="tasks-tab-bar">
        <button
          className={`tasks-tab ${tab === "created" ? "tasks-tab-active" : ""}`}
          onClick={() => setTab("created")}
        >
          Created by Me
          {createdTasks.length > 0 && (
            <span className="tasks-tab-count">{createdTasks.length}</span>
          )}
        </button>
        <button
          className={`tasks-tab ${tab === "assigned" ? "tasks-tab-active" : ""}`}
          onClick={() => setTab("assigned")}
        >
          Assigned to Me
          {assignedTasks.length > 0 && (
            <span className="tasks-tab-count">{assignedTasks.length}</span>
          )}
        </button>
      </div>

      {loading && <div className="tasks-loading">Loading...</div>}

      {!loading && tasks.length === 0 && (
        <div className="tasks-empty">
          {tab === "created"
            ? "No tasks created yet. Click \"+ Assign New Task\" to get started."
            : "No tasks assigned to you."}
        </div>
      )}

      <div className="tasks-list">
        {tasks.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            isOwner={tab === "created"}
            onEdit={() => nav(`edit/${t.id}`)}
            onDelete={() => handleDelete(t.id, t.title)}
            onNotify={() => handleNotify(t.id)}
            notifying={notifyingId === t.id}
          />
        ))}
      </div>
    </div>
  );
}

function TaskCard({ task, isOwner, onEdit, onDelete, onNotify, notifying }) {
  const [expanded, setExpanded] = useState(false);

  const overdue =
    task.endDate &&
    task.status !== "DONE" &&
    task.status !== "CANCELLED" &&
    new Date(task.endDate) < new Date(new Date().toDateString());

  return (
    <div className={`task-card ${overdue ? "task-card-overdue" : ""}`}>
      <div className="task-card-top">
        <div className="task-card-title-row">
          <span className="task-card-title">{task.title}</span>
          <div className="task-card-badges">
            <span className={`task-badge ${PRIORITY_COLORS[task.priority] || ""}`}>
              {task.priority}
            </span>
            <span className={`task-badge ${STATUS_COLORS[task.status] || ""}`}>
              {task.status?.replace("_", " ")}
            </span>
          </div>
        </div>

        <div className="task-card-meta">
          {task.startDate && <span>Start: {task.startDate}</span>}
          {task.endDate && (
            <span className={overdue ? "task-meta-overdue" : ""}>
              Due: {task.endDate}
            </span>
          )}
          {task.creatorUsername && !isOwner && (
            <span>From: {task.creatorUsername}</span>
          )}
        </div>

        <div className="task-card-assignees">
          {task.assignees?.map((a) => (
            <span key={a} className="task-assignee-chip">{a}</span>
          ))}
        </div>
      </div>

      {task.description && (
        <div className="task-card-description">{task.description}</div>
      )}

      {task.followUps?.length > 0 && (
        <div className="task-card-followups">
          <button
            className="task-card-toggle"
            onClick={() => setExpanded((p) => !p)}
            type="button"
          >
            {expanded ? "Hide" : "Show"} follow-ups ({task.followUps.length})
          </button>
          {expanded && (
            <ul className="task-followup-list">
              {task.followUps.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isOwner && (
        <div className="task-card-actions">
          <button className="task-card-btn" onClick={onEdit}>Edit</button>
          <button
            className="task-card-btn task-card-btn-notify"
            onClick={onNotify}
            disabled={notifying}
          >
            {notifying ? "Sending..." : "Send Follow-up Email"}
          </button>
          <button className="task-card-btn task-card-btn-delete" onClick={onDelete}>
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
